import type { Schritt } from "@fbs/funnel-schema";
import { useRef } from "preact/hooks";

import { alsText, alsZahl, fehlerText, type SchrittProps } from "./gemeinsam";

type Freitext = Extract<Schritt, { type: "text" }>;
type Zahl = Extract<Schritt, { type: "number" }>;
type Postleitzahl = Extract<Schritt, { type: "postcode" }>;
type Datum = Extract<Schritt, { type: "date" }>;

/**
 * Getippte Antworten gehen nicht von selbst weiter.
 *
 * Bei einer Einfachauswahl ist der Tipp die Antwort, da ist der automatische
 * Übergang eine Erleichterung. Bei Eingaben ist er eine Falle: Wer sich
 * vertippt, wird weitergeschoben, bevor er es merkt. Deshalb hier immer der
 * bewusste Tipp auf "Weiter".
 */

export function FreitextAnsicht({
  schritt,
  wert,
  fehler,
  onWert,
}: SchrittProps<Freitext>) {
  const text = alsText(wert);
  const meldung = fehlerText(fehler);
  const uebrig = schritt.max_length - text.length;

  return (
    <div class="f-feld">
      {schritt.multiline ? (
        <textarea
          class={`f-eingabe f-eingabe--mehrzeilig${meldung ? " f-eingabe--fehler" : ""}`}
          value={text}
          rows={5}
          maxLength={schritt.max_length}
          placeholder={schritt.placeholder}
          aria-label={schritt.question}
          aria-invalid={meldung !== null}
          onInput={(e) => {
            onWert(e.currentTarget.value);
          }}
        />
      ) : (
        <input
          type="text"
          class={`f-eingabe${meldung ? " f-eingabe--fehler" : ""}`}
          value={text}
          maxLength={schritt.max_length}
          placeholder={schritt.placeholder}
          aria-label={schritt.question}
          aria-invalid={meldung !== null}
          autoComplete="off"
          onInput={(e) => {
            onWert(e.currentTarget.value);
          }}
        />
      )}

      {/* Der Zähler erscheint erst kurz vor dem Limit. Dauerhaft sichtbar
          wirkt er wie eine Vorgabe, möglichst viel zu schreiben. */}
      {uebrig < 80 ? <p class="f-zaehler">Noch {uebrig} Zeichen</p> : null}
      {meldung ? <p class="f-fehler">{meldung}</p> : null}
    </div>
  );
}

export function ZahlAnsicht({ schritt, wert, fehler, onWert }: SchrittProps<Zahl>) {
  const meldung = fehlerText(fehler);

  // Siehe alsZahl(): "noch nichts eingegeben" ist nicht dasselbe wie null.
  const { hatWert, zahl } = alsZahl(wert);

  const grenzen = (kandidat: number): number => {
    let ergebnis = kandidat;
    if (schritt.min !== undefined) ergebnis = Math.max(schritt.min, ergebnis);
    if (schritt.max !== undefined) ergebnis = Math.min(schritt.max, ergebnis);
    // Gleitkommareste wie 12.300000000000001 vermeiden.
    return Math.round(ergebnis * 1000) / 1000;
  };

  const versetzen = (richtung: 1 | -1) => {
    // Der erste Tipp landet auf dem Minimum, nicht einen Schritt darüber.
    // Sonst überspringt man mit dem ersten Plus den häufigsten Fall.
    if (!hatWert) {
      onWert(grenzen(schritt.min ?? 0));
      return;
    }
    onWert(grenzen(zahl + richtung * schritt.step));
  };

  if (
    schritt.input_mode === "slider" &&
    schritt.min !== undefined &&
    schritt.max !== undefined
  ) {
    const stand = hatWert ? zahl : Math.round((schritt.min + schritt.max) / 2);
    return (
      <div class="f-feld">
        <p class="f-schieber__wert">
          {stand}
          {schritt.unit ? <span class="f-einheit"> {schritt.unit}</span> : null}
        </p>
        <input
          type="range"
          class="f-schieber"
          min={schritt.min}
          max={schritt.max}
          step={schritt.step}
          value={stand}
          aria-label={schritt.question}
          onInput={(e) => {
            onWert(Number(e.currentTarget.value));
          }}
        />
        <div class="f-schieber__enden">
          <span>{schritt.min}</span>
          <span>{schritt.max}</span>
        </div>
        {meldung ? <p class="f-fehler">{meldung}</p> : null}
      </div>
    );
  }

  if (schritt.input_mode === "stepper") {
    return (
      <div class="f-feld">
        <div class="f-stepper">
          <button
            type="button"
            class="f-stepper__taste"
            aria-label="Weniger"
            onClick={() => {
              versetzen(-1);
            }}
          >
            −
          </button>
          <span class="f-stepper__wert">
            {hatWert ? zahl : "–"}
            {schritt.unit ? <span class="f-einheit"> {schritt.unit}</span> : null}
          </span>
          <button
            type="button"
            class="f-stepper__taste"
            aria-label="Mehr"
            onClick={() => {
              versetzen(1);
            }}
          >
            +
          </button>
        </div>
        {meldung ? <p class="f-fehler">{meldung}</p> : null}
      </div>
    );
  }

  return (
    <div class="f-feld">
      <div class="f-eingabe__zeile">
        <input
          type="text"
          // Kein type="number": Das blendet auf dem Handy eine Tastatur mit
          // Pfeilen ein, verschluckt in manchen Browsern Eingaben und lässt
          // sich nicht zuverlässig auslesen. inputMode bringt dieselbe
          // Zifferntastatur ohne diese Nebenwirkungen.
          inputMode="decimal"
          class={`f-eingabe${meldung ? " f-eingabe--fehler" : ""}`}
          value={hatWert ? String(zahl) : ""}
          aria-label={schritt.question}
          aria-invalid={meldung !== null}
          onInput={(e) => {
            const roh = e.currentTarget.value.replace(",", ".");
            onWert(roh === "" ? "" : Number(roh));
          }}
        />
        {schritt.unit ? <span class="f-eingabe__einheit">{schritt.unit}</span> : null}
      </div>
      {meldung ? <p class="f-fehler">{meldung}</p> : null}
    </div>
  );
}

/**
 * Die Antwort eines Postleitzahlschritts.
 *
 * Ohne Zusatzfelder bleibt es eine Zeichenkette - so steht es in den zwölf
 * anderen Funnels und in jeder gespeicherten Sitzung. Mit Zusatzfeldern wird
 * ein Objekt daraus. Beide Formen müssen gelesen werden können, sonst verliert
 * jemand mitten im Ausfüllen seine Eingaben, weil zwischendurch ausgeliefert
 * wurde.
 */
export function adresseLesen(wert: unknown): {
  postcode: string;
  city: string;
  street: string;
} {
  if (typeof wert === "string") return { postcode: wert, city: "", street: "" };
  if (typeof wert === "object" && wert !== null && !Array.isArray(wert)) {
    const o = wert as Record<string, unknown>;
    const t = (k: string) => (typeof o[k] === "string" ? o[k] : "");
    return { postcode: t("postcode"), city: t("city"), street: t("street") };
  }
  return { postcode: "", city: "", street: "" };
}

/** Die Zeichen vor den Feldern. Gleiche Strichstärke wie im übrigen Auftritt. */
const ADRESSZEICHEN: Record<"postcode" | "city" | "street", preact.JSX.Element> = {
  postcode: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  ),
  city: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3.4 20.4V9.6l6-3.6v14.4M9.4 20.4V11l7.2-3.4v12.8M3.4 20.4h17.2" />
      <path d="M12.6 12.6h1.2M12.6 16h1.2M6 12.6h1.2M6 16h1.2" />
    </svg>
  ),
  street: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3.6 20.4 8.4 3.6M20.4 20.4 15.6 3.6" />
      <path d="M12 4.6v2.6M12 10.8v2.6M12 17v2.6" />
    </svg>
  ),
};

const ADRESSTEXTE: Record<
  "city" | "street",
  { beschriftung: string; ergaenzung: string }
> = {
  city: { beschriftung: "Stadt", ergaenzung: "address-level2" },
  street: { beschriftung: "Straße und Hausnummer", ergaenzung: "street-address" },
};

export function PostleitzahlAnsicht({
  schritt,
  wert,
  fehler,
  onWert,
  onWeiter,
}: SchrittProps<Postleitzahl>) {
  // Ohne Feldnamen: die Meldung zur Postleitzahl und der Pflichthinweis,
  // wenn noch gar nichts eingegeben wurde.
  const meldung = fehlerText(fehler);
  const adresse = adresseLesen(wert);
  const feld = useRef<HTMLInputElement>(null);
  const zusatz = schritt.address_fields;

  /**
   * Ohne Zusatzfelder bleibt die Antwort eine Zeichenkette.
   *
   * Sonst bekäme jeder andere Funnel ein Objekt, wo bisher Text stand - und
   * alles, was daran hängt, müsste mitwandern, ohne dass sich für den
   * Besucher etwas ändert.
   */
  const setzen = (teil: Partial<typeof adresse>) => {
    const neu = { ...adresse, ...teil };
    onWert(zusatz.length === 0 ? neu.postcode : neu);
  };

  return (
    <div class="f-feld f-adresse">
      <div class="f-adresse__zeile">
        <span class="f-adresse__zeichen">{ADRESSZEICHEN.postcode}</span>
        <input
          ref={feld}
          type="text"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={5}
          class={`f-eingabe f-eingabe--plz${fehlerText(fehler, "postcode") ? " f-eingabe--fehler" : ""}`}
          value={adresse.postcode}
          placeholder="Postleitzahl"
          aria-label={zusatz.length === 0 ? schritt.question : "Postleitzahl"}
          aria-invalid={fehlerText(fehler, "postcode") !== null}
          onInput={(e) => {
            // Alles außer Ziffern fällt beim Tippen weg. Wer eine Adresse aus
            // der Zwischenablage einfügt, soll nicht erst aufräumen müssen.
            const sauber = e.currentTarget.value.replace(/\D/g, "").slice(0, 5);
            e.currentTarget.value = sauber;
            setzen({ postcode: sauber });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") onWeiter();
          }}
        />
        {fehlerText(fehler, "postcode") ? (
          <p class="f-fehler">{fehlerText(fehler, "postcode")}</p>
        ) : null}
      </div>

      {zusatz.map((f) => (
        <div class="f-adresse__zeile" key={f.name}>
          <span class="f-adresse__zeichen">{ADRESSZEICHEN[f.name]}</span>
          <input
            type="text"
            aria-invalid={fehlerText(fehler, f.name) !== null}
            autoComplete={ADRESSTEXTE[f.name].ergaenzung}
            maxLength={200}
            class={`f-eingabe${fehlerText(fehler, f.name) ? " f-eingabe--fehler" : ""}`}
            value={adresse[f.name]}
            placeholder={
              (f.label ?? ADRESSTEXTE[f.name].beschriftung) +
              (f.required ? "" : " (optional)")
            }
            aria-label={f.label ?? ADRESSTEXTE[f.name].beschriftung}
            onInput={(e) => {
              setzen({ [f.name]: e.currentTarget.value });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") onWeiter();
            }}
          />
          {/*
            Die Meldung unter dem Feld, das sie meint. Sie wurde vorher zwar
            erzeugt, aber nirgends gezeigt: Unten stand nur die Meldung ohne
            Feldbezug, also die zur Postleitzahl. Wer Ort und Straße leer ließ,
            kam nicht weiter und las nirgends, warum.
          */}
          {fehlerText(fehler, f.name) ? (
            <p class="f-fehler">{fehlerText(fehler, f.name)}</p>
          ) : null}
        </div>
      ))}

      {meldung ? <p class="f-fehler">{meldung}</p> : null}
    </div>
  );
}

export function DatumAnsicht({ schritt, wert, fehler, onWert }: SchrittProps<Datum>) {
  const meldung = fehlerText(fehler);

  return (
    <div class="f-feld">
      <input
        type={schritt.mode === "month" ? "month" : "date"}
        class={`f-eingabe${meldung ? " f-eingabe--fehler" : ""}`}
        value={alsText(wert)}
        min={schritt.min}
        max={schritt.max}
        aria-label={schritt.question}
        aria-invalid={meldung !== null}
        onInput={(e) => {
          onWert(e.currentTarget.value);
        }}
      />
      {meldung ? <p class="f-fehler">{meldung}</p> : null}
    </div>
  );
}
